import { useState } from "react";
import type { CoverageGapReason } from "@skills-marketplace/contracts/contract-10-maintainers";
import {
  getMaintainerCoverage,
  GitHubProxyError,
  type MaintainerCoverageResponse,
} from "../lib/github-proxy-client";
import { reportError } from "../lib/newrelic";
import styles from "./MaintainerCoverageSection.module.css";

export const REASON_LABELS: Readonly<Record<CoverageGapReason, string>> = {
  no_identity: "No manifest email or human committer",
  none_in_directory: "No candidate is in the directory",
  all_left: "Every maintainer has left",
  not_evaluated: "Not evaluated yet",
};

function isKnownReason(reason: string): reason is CoverageGapReason {
  return Object.hasOwn(REASON_LABELS, reason);
}

// A reason added after this build shows as its raw value rather than breaking the table.
function reasonLabel(reason: string): string {
  return isKnownReason(reason) ? REASON_LABELS[reason] : reason;
}

export default function MaintainerCoverageSection() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<MaintainerCoverageResponse | null>(null);
  const [error, setError] = useState("");

  async function run() {
    setLoading(true);
    setError("");
    try {
      setReport(await getMaintainerCoverage());
    } catch (err) {
      setReport(null);
      // The status separates the proxy's own 403 and 5xx from the WAF and network failures.
      if (err instanceof GitHubProxyError) {
        reportError(err, "maintainer-coverage-fetch", { status: err.statusCode });
      } else {
        reportError(err, "maintainer-coverage-fetch");
      }
      // The proxy's message names the cause (not an admin, directory down); a WAF or
      // undeployed-route 403 has no such body, so a fixed "not an admin" line would mislead.
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="maintainer-coverage-heading">
      <h2 id="maintainer-coverage-heading" className={styles.heading}>
        Admin: Maintainer Coverage
      </h2>
      <p className={styles.description}>
        Active, visible plugins outside the contributed-skills repo, and which of them have no
        maintainer still in the company directory.
      </p>

      <button type="button" className={styles.button} onClick={run} disabled={loading}>
        {loading ? "Running..." : "Run coverage report"}
      </button>

      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}

      {report && (
        <div className={styles.result}>
          <p className={styles.summary}>
            {report.covered} of {report.total} plugins covered
            {report.total > 0 && ` (${((report.covered / report.total) * 100).toFixed(1)}%)`}
          </p>
          {report.uncovered.length === 0 ? (
            <p>Every counted plugin has a maintainer in the directory.</p>
          ) : (
            <table className={styles.table}>
              <caption className={styles.caption}>Uncovered plugins ({report.uncovered.length})</caption>
              <thead>
                <tr>
                  <th scope="col">Plugin</th>
                  <th scope="col">Repo</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {report.uncovered.map((p) => (
                  <tr key={p.pk}>
                    <td>{p.displayName}</td>
                    <td className={styles.mono}>
                      {p.orgOrOwner}/{p.repoName}
                      {p.skillPath && `/${p.skillPath}`}
                    </td>
                    <td>{reasonLabel(p.reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
